/**
 * SILLY ANIMAL GENERATOR (SillyGenerator)
 *
 * A creative game where users can:
 * - Mix animal superpowers (Animal A gets Animal B's superpower)
 * - Generate silly situations (N animals doing funny things)
 * - Order custom illustrations of their creations
 *
 * Features:
 * - Soft nature-inspired animated background (flowers, bubbles, waves)
 * - Bilingual support (English/Spanish)
 * - Sound effects with Tone.js
 * - Order illustration modal
 * - Ages 5-10
 */

import React, { useState, useEffect, useRef } from 'react';
import * as Tone from 'tone';
import { AnimatedFlowerBackground } from '../backgrounds';
import { EnhancedDrawingCanvas, type EnhancedDrawingCanvasRef } from './EnhancedDrawingCanvas';

// ============================================
// TYPE DEFINITIONS
// ============================================

interface Animal {
  letter: string;
  name: string;
  nameEs: string;
  power: string;
  powerEs: string;
  emoji: string;
  svg: string | React.ReactNode | null;
}

interface Situation {
  en: string;
  es: string;
}

interface Question {
  en: string;
  es: string;
}

interface Translation {
  en: string;
  es: string;
}

interface SuperpowerResult {
  type: 'superpower';
  animal1: Animal;
  animal2: Animal;
  question: Question;
}

interface SituationResult {
  type: 'situation';
  animal: Animal;
  number: number;
  situation: Situation;
  question: Question;
}

type GameResult = SuperpowerResult | SituationResult;

interface SillyAnimalGeneratorProps {
  onExit?: () => void;
  language?: 'en' | 'es';
  onLanguageToggle?: () => void;
}

// ============================================
// ANIMAL DATA
// ============================================

const animals: Animal[] = [
  { letter: 'A', name: 'Axolotl', nameEs: 'Ajolote', power: 'regrow body parts', powerEs: 'regenerar partes del cuerpo', emoji: '🦎', svg: null },
  { letter: 'B', name: 'Bat', nameEs: 'Murciélago', power: 'see in the dark', powerEs: 'ver en la oscuridad', emoji: '🦇', svg: null },
  { letter: 'C', name: 'Chameleon', nameEs: 'Camaleón', power: 'change colors', powerEs: 'cambiar de colores', emoji: '🦎', svg: null },
  { letter: 'D', name: 'Dolphin', nameEs: 'Delfín', power: 'be super smart', powerEs: 'ser súper inteligente', emoji: '🐬', svg: null },
  { letter: 'E', name: 'Elephant', nameEs: 'Elefante', power: 'never forget anything', powerEs: 'nunca olvidar nada', emoji: '🐘', svg: null },
  { letter: 'F', name: 'Firefly', nameEs: 'Luciérnaga', power: 'glow in the dark', powerEs: 'brillar en la oscuridad', emoji: '✨', svg: null },
  { letter: 'G', name: 'Giraffe', nameEs: 'Jirafa', power: 'reach super high', powerEs: 'alcanzar súper alto', emoji: '🦒', svg: null },
  { letter: 'H', name: 'Hummingbird', nameEs: 'Colibrí', power: 'fly backwards', powerEs: 'volar hacia atrás', emoji: '🐦', svg: null },
  { letter: 'I', name: 'Iguana', nameEs: 'Iguana', power: 'stay perfectly still', powerEs: 'quedarse muy quieto', emoji: '🦎', svg: null },
  { letter: 'J', name: 'Jaguar', nameEs: 'Jaguar', power: 'run super fast', powerEs: 'correr súper rápido', emoji: '🐆', svg: null },
  { letter: 'K', name: 'Kangaroo', nameEs: 'Canguro', power: 'super jump', powerEs: 'súper saltar', emoji: '🦘', svg: null },
  { letter: 'L', name: 'Lion', nameEs: 'León', power: 'roar super loud', powerEs: 'rugir muy fuerte', emoji: '🦁', svg: null },
  { letter: 'M', name: 'Monkey', nameEs: 'Mono', power: 'climb anything', powerEs: 'trepar cualquier cosa', emoji: '🐒', svg: null },
  { letter: 'N', name: 'Narwhal', nameEs: 'Narval', power: 'have a magic horn', powerEs: 'tener un cuerno mágico', emoji: '🐋', svg: null },
  { letter: 'O', name: 'Owl', nameEs: 'Búho', power: 'spin head all around', powerEs: 'girar la cabeza completamente', emoji: '🦉', svg: null },
  { letter: 'P', name: 'Porcupine', nameEs: 'Puercoespín', power: 'have spiky armor', powerEs: 'tener armadura de púas', emoji: '🦔', svg: null },
  { letter: 'Q', name: 'Quokka', nameEs: 'Quokka', power: 'make everyone smile', powerEs: 'hacer sonreír a todos', emoji: '😊', svg: null },
  { letter: 'R', name: 'Rhino', nameEs: 'Rinoceronte', power: 'be super strong', powerEs: 'ser súper fuerte', emoji: '🦏', svg: null },
  { letter: 'S', name: 'Skunk', nameEs: 'Zorrillo', power: 'spray stinky stuff', powerEs: 'rociar cosas apestosas', emoji: '🦨', svg: null },
  { letter: 'T', name: 'Turtle', nameEs: 'Tortuga', power: 'carry a shield', powerEs: 'cargar un escudo', emoji: '🐢', svg: null },
  { letter: 'U', name: 'Umbrellabird', nameEs: 'Pájaro Paraguas', power: 'stay dry in rain', powerEs: 'mantenerse seco en la lluvia', emoji: '☂️', svg: null },
  { letter: 'V', name: 'Vulture', nameEs: 'Buitre', power: 'see super far away', powerEs: 'ver súper lejos', emoji: '🦅', svg: null },
  { letter: 'W', name: 'Whale', nameEs: 'Ballena', power: 'hold breath forever', powerEs: 'aguantar la respiración para siempre', emoji: '🐋', svg: null },
  { letter: 'Z', name: 'Zebra', nameEs: 'Cebra', power: 'confuse with stripes', powerEs: 'confundir con rayas', emoji: '🦓', svg: null },
];

const situations: Situation[] = [
  { en: 'baking cupcakes', es: 'horneando pastelitos' },
  { en: 'dancing ballet', es: 'bailando ballet' },
  { en: 'riding tiny bicycles', es: 'montando bicicletas pequeñitas' },
  { en: 'singing in the shower', es: 'cantando en la ducha' },
  { en: 'playing hide and seek', es: 'jugando a las escondidas' },
  { en: 'doing yoga', es: 'haciendo yoga' },
  { en: 'building a sandcastle', es: 'construyendo un castillo de arena' },
  { en: 'jumping on a trampoline', es: 'saltando en un trampolín' },
  { en: 'having a tea party', es: 'teniendo una fiesta de té' },
  { en: 'playing drums', es: 'tocando la batería' },
  { en: 'painting a mural', es: 'pintando un mural' },
  { en: 'flying kites', es: 'volando cometas' },
  { en: 'wearing silly hats', es: 'usando sombreros chistosos' },
  { en: 'doing a fashion show', es: 'haciendo un desfile de moda' },
  { en: 'brushing their teeth', es: 'cepillándose los dientes' },
  { en: 'taking a bubble bath', es: 'tomando un baño de burbujas' },
  { en: 'reading bedtime stories', es: 'leyendo cuentos para dormir' },
  { en: 'eating spaghetti', es: 'comiendo espaguetis' },
  { en: 'playing video games', es: 'jugando videojuegos' },
  { en: 'running a race', es: 'corriendo una carrera' },
  { en: 'blowing bubbles', es: 'soplando burbujas' },
  { en: 'skateboarding', es: 'andando en patineta' },
  { en: 'making pizza', es: 'haciendo pizza' },
  { en: 'sleeping in bunk beds', es: 'durmiendo en literas' },
];

const superpowerQuestions: Question[] = [
  { en: 'What would YOU do with that superpower?', es: '¿Qué harías TÚ con ese superpoder?' },
  { en: 'Where would you go with that superpower?', es: '¿A dónde irías con ese superpoder?' },
  { en: 'Who would you help with that superpower?', es: '¿A quién ayudarías con ese superpoder?' },
  { en: 'What game would you play with that superpower?', es: '¿Qué juego jugarías con ese superpoder?' },
  { en: 'What problem would you solve with that superpower?', es: '¿Qué problema resolverías con ese superpoder?' },
  { en: 'Would you tell your friends about your superpower?', es: '¿Les contarías a tus amigos sobre tu superpoder?' },
  { en: 'What silly thing would you do with that superpower?', es: '¿Qué cosa chistosa harías con ese superpoder?' },
  { en: 'How would that superpower help you at school?', es: '¿Cómo te ayudaría ese superpoder en la escuela?' },
  { en: 'What adventure would you go on with that superpower?', es: '¿Qué aventura tendrías con ese superpoder?' },
  { en: 'Would this superpower be better at day or night?', es: '¿Este superpoder sería mejor de día o de noche?' },
];

const situationQuestions: Question[] = [
  { en: 'What would that look like?', es: '¿Cómo se vería eso?' },
  { en: 'What sounds would they make?', es: '¿Qué sonidos harían?' },
  { en: 'Would it be messy or neat?', es: '¿Sería desordenado o ordenado?' },
  { en: 'Where would this happen?', es: '¿Dónde pasaría esto?' },
  { en: 'What would be the funniest part?', es: '¿Cuál sería la parte más chistosa?' },
  { en: 'Would you want to join them?', es: '¿Te gustaría unirte a ellos?' },
  { en: 'What would happen next?', es: '¿Qué pasaría después?' },
  { en: 'How long would it take?', es: '¿Cuánto tiempo tomaría?' },
  { en: 'Would they need help?', es: '¿Necesitarían ayuda?' },
  { en: 'What would make it even sillier?', es: '¿Qué lo haría aún más chistoso?' },
];

// UI translations - consolidated to avoid repetition
const ui: Record<string, Translation> = {
  title: { en: 'Mixery', es: 'Mezclería' },
  superpowers: { en: '⚡ Superpowers', es: '⚡ Superpoderes' },
  sillySituations: { en: '🎭 Silly Situations', es: '🎭 Situaciones Locas' },
  generate: { en: 'Mix it up!', es: '¡Mézclalo!' },
  gotPower: { en: 'got the', es: 'obtuvo el superpoder del' },
  superpower: { en: 'superpower!', es: '!' },
  nowItCan: { en: 'Now it can', es: '¡Ahora puede' },
  didYouKnow: { en: 'Did you know?', es: '¿Sabías que...?' },
  canNormally: { en: 'can normally', es: 'normalmente pueden' },
  funFact: { en: 'Fun fact!', es: '¡Dato curioso!' },
  thinkAbout: { en: '🤔 Think about it:', es: '🤔 Piensa en esto:' },
  instructions: { en: 'Mix up some animal magic! Ready for a wild surprise?', es: '¡Mezcla un poco de magia animal! ¿Listo para una sorpresa salvaje?' },
  superpowerDesc: { en: 'Animals get powers from other animals!', es: '¡Los animales obtienen poderes de otros animales!' },
  situationDesc: { en: 'Random animals doing funny things!', es: '¡Animales haciendo cosas chistosas!' },
  soundOn: { en: '🔊 Sound', es: '🔊 Sonido' },
  soundOff: { en: '🔇 Sound', es: '🔇 Sonido' },
  drawYourOwn: { en: '🎨 Draw Your Own!', es: '🎨 ¡Dibuja el Tuyo!' },
  hideDrawing: { en: '✨ Hide Canvas', es: '✨ Ocultar Lienzo' },
  drawingTitle: { en: '🎨 Draw Your Creation!', es: '🎨 ¡Dibuja Tu Creación!' },
  drawingSubtitle: { en: 'Bring your silly combination to life!', es: '¡Dale vida a tu combinación!' },
  backHome: { en: '← Back to Games', es: '← Volver a Juegos' },
};

// Animal icon component
interface AnimalIconProps {
  animal: Animal;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
}

const AnimalIcon: React.FC<AnimalIconProps> = ({ animal, size = 'large' }) => {
  const sizeClasses = {
    small: 'w-8 h-8',
    medium: 'w-12 h-12',
    large: 'w-16 h-16',
    xlarge: 'w-20 h-20',
  };

  // If SVG/photo is available, show it (for future photographic images)
  if (animal.svg) {
    if (typeof animal.svg === 'string') {
      return <img src={animal.svg} alt={animal.name} className={`${sizeClasses[size]} object-contain`} />;
    }
    return <div className={sizeClasses[size]}>{animal.svg}</div>;
  }

  const emojiSizes = { small: 'text-4xl', medium: 'text-5xl', large: 'text-6xl md:text-7xl', xlarge: 'text-7xl md:text-8xl' };
  return (
    <span className={`${sizeClasses[size]} inline-flex items-center justify-center ${emojiSizes[size]}`}>
      {animal.emoji}
    </span>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export default function SillyAnimalGenerator({ onExit, language: propLanguage, onLanguageToggle }: SillyAnimalGeneratorProps) {
  const [result, setResult] = useState<GameResult | null>(null);
  const [mode, setMode] = useState<'superpower' | 'situation'>('superpower');
  const [lang, setLang] = useState<'en' | 'es'>(propLanguage || 'en');
  const [soundOn, setSoundOn] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showDrawing, setShowDrawing] = useState(false);
  const canvasRef = useRef<EnhancedDrawingCanvasRef>(null);

  // Load Fredoka font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Fredoka:wght@700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  // Sync with external language prop
  useEffect(() => {
    if (propLanguage) {
      setLang(propLanguage);
    }
  }, [propLanguage]);

  const synth = new Tone.Synth().toDestination();
  const membraneSynth = new Tone.MembraneSynth().toDestination();

  const playGenerateSound = async () => {
    if (!soundOn) return;
    await Tone.start();
    const now = Tone.now();
    synth.triggerAttackRelease('C5', '16n', now);
    synth.triggerAttackRelease('E5', '16n', now + 0.1);
    synth.triggerAttackRelease('G5', '16n', now + 0.2);
    synth.triggerAttackRelease('C6', '8n', now + 0.3);
  };

  const playModeSwitch = async () => {
    if (!soundOn) return;
    await Tone.start();
    synth.triggerAttackRelease('G4', '16n');
  };

  const t = (key: string): string => ui[key]?.[lang] || key;
  const getName = (animal: Animal): string => lang === 'es' ? animal.nameEs : animal.name;
  const getPower = (animal: Animal): string => lang === 'es' ? animal.powerEs : animal.power;

  const generateSuperpower = () => {
    const animal1 = animals[Math.floor(Math.random() * animals.length)];
    let animal2 = animals[Math.floor(Math.random() * animals.length)];
    while (animal2.name === animal1.name) {
      animal2 = animals[Math.floor(Math.random() * animals.length)];
    }
    const question = superpowerQuestions[Math.floor(Math.random() * superpowerQuestions.length)];

    setResult({ type: 'superpower', animal1, animal2, question });
  };

  const generateSituation = () => {
    const animal = animals[Math.floor(Math.random() * animals.length)];
    const number = Math.floor(Math.random() * 10) + 1;
    const situation = situations[Math.floor(Math.random() * situations.length)];
    const question = situationQuestions[Math.floor(Math.random() * situationQuestions.length)];

    setResult({ type: 'situation', animal, number, situation, question });
  };

  const handleGenerate = async () => {
    setIsAnimating(true);
    await playGenerateSound();

    setTimeout(() => {
      if (mode === 'superpower') {
        generateSuperpower();
      } else {
        generateSituation();
      }
      setIsAnimating(false);
    }, 400);
  };

  const handleModeSwitch = (newMode: 'superpower' | 'situation') => {
    setMode(newMode);
    playModeSwitch();
  };

  const getPlural = (animal: Animal, number: number): string => {
    if (lang === 'es') {
      if (number === 1) return animal.nameEs;
      const name = animal.nameEs;
      if (name.endsWith('z')) return name.slice(0, -1) + 'ces';
      if (name.endsWith('s')) return name;
      if (name.match(/[aeiou]$/)) return name + 's';
      return name + 'es';
    } else {
      if (number === 1) return animal.name;
      return animal.name + 's';
    }
  };

  const getComboDescription = (): string => {
    if (!result) return '';
    if (result.type === 'superpower') {
      return lang === 'es'
        ? `${result.animal1.emoji} ${getName(result.animal1)} con el superpoder de ${result.animal2.emoji} ${getName(result.animal2)} - ¡Puede ${getPower(result.animal2)}!`
        : `${result.animal1.emoji} ${getName(result.animal1)} with ${result.animal2.emoji} ${getName(result.animal2)}'s superpower - Can ${getPower(result.animal2)}!`;
    } else {
      return lang === 'es'
        ? `${result.animal.emoji} ${result.number} ${getPlural(result.animal, result.number)} ${result.situation.es}`
        : `${result.animal.emoji} ${result.number} ${getPlural(result.animal, result.number)} ${result.situation.en}`;
    }
  };

  return (
    <div className="min-h-screen p-4 flex flex-col items-center relative">
      <div className="fixed inset-0 -z-10">
        <AnimatedFlowerBackground theme="bubbles" />
      </div>

      {/* Sound Toggle Control */}
      <div className="w-full max-w-md flex justify-end mb-4">
        <button
          onClick={() => setSoundOn(!soundOn)}
          className={`px-4 py-2 rounded-full font-bold shadow-md hover:scale-105 transition-all ${
            soundOn ? 'bg-green-400 text-white' : 'bg-gray-300 text-gray-600'
          }`}
        >
          {soundOn ? t('soundOn') : t('soundOff')}
        </button>
      </div>

      <h1
        className="text-7xl md:text-9xl font-bold mb-4 text-center text-emerald-800"
        style={{
          fontFamily: "'Fredoka', sans-serif",
          textShadow: '1px 1px 2px rgba(255, 255, 255, 0.8)'
        }}
      >
        {t('title')}
      </h1>

      {/* Mode Toggle */}
      <div className="flex gap-8 mb-6">
        <button
          onClick={() => handleModeSwitch('superpower')}
          className={`px-12 py-6 rounded-full font-bold text-4xl transition-all shadow-lg ${
            mode === 'superpower'
              ? 'bg-purple-600 text-white scale-110'
              : 'bg-white text-purple-600 hover:bg-purple-100'
          }`}
        >
          {t('superpowers')}
        </button>
        <button
          onClick={() => handleModeSwitch('situation')}
          className={`px-12 py-6 rounded-full font-bold text-4xl transition-all shadow-lg ${
            mode === 'situation'
              ? 'bg-orange-500 text-white scale-110'
              : 'bg-white text-orange-500 hover:bg-orange-100'
          }`}
        >
          {t('sillySituations')}
        </button>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={isAnimating}
        className={`bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white text-3xl font-bold py-4 px-8 rounded-full shadow-xl border-2 border-white transform transition-all mb-6 ${
          isAnimating ? 'scale-95 opacity-75' : 'hover:scale-105'
        }`}
      >
        {isAnimating ? '✨✨✨' : t('generate')}
      </button>

      {/* Result Card - Responsive sizing */}
      {result && (
        <div className={`bg-white rounded-3xl shadow-2xl p-5 md:p-8 lg:p-10 w-full max-w-md md:max-w-2xl lg:max-w-4xl xl:max-w-5xl transition-all ${
          isAnimating ? 'scale-95 opacity-50' : 'scale-100 opacity-100'
        }`}>
          {result.type === 'superpower' ? (
            <>
              <div className="text-center mb-4 flex items-center justify-center gap-2">
                <AnimalIcon animal={result.animal1} size="large" />
                <span className="text-6xl">+</span>
                <AnimalIcon animal={result.animal2} size="large" />
              </div>

              <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl p-6 md:p-8 mb-4">
                <p className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-center">
                  <span className="font-bold text-purple-600">{result.animal1.emoji} {getName(result.animal1)}</span>
                  {' '}{t('gotPower')}{' '}
                  <span className="font-bold text-pink-600">{result.animal2.emoji} {getName(result.animal2)}</span>
                  {lang === 'en' ? "'s superpower!" : '!'}
                </p>
                <p className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-center font-bold text-gray-800 mt-4">
                  {t('nowItCan')} {getPower(result.animal2)}{lang === 'es' ? '!' : '!'}
                </p>
              </div>

              <div className="bg-yellow-100 rounded-2xl p-6 mb-4">
                <p className="text-2xl md:text-3xl text-gray-600 mb-2">{t('didYouKnow')}</p>
                <p className="text-3xl md:text-4xl lg:text-5xl">
                  <span className="font-bold">{result.animal1.emoji} {getName(result.animal1)}{lang === 'es' ? 's' : 's'}</span> {t('canNormally')} {getPower(result.animal1)}!
                </p>
                <p className="text-3xl md:text-4xl lg:text-5xl mt-2">
                  <span className="font-bold">{result.animal2.emoji} {getName(result.animal2)}{lang === 'es' ? 's' : 's'}</span> {t('canNormally')} {getPower(result.animal2)}!
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-4 flex flex-col items-center">
                <AnimalIcon animal={result.animal} size="xlarge" />
                <div className="text-8xl md:text-9xl lg:text-9xl font-bold text-orange-500 mt-2">
                  × {result.number}
                </div>
              </div>

              <div className="bg-gradient-to-r from-orange-100 to-yellow-100 rounded-2xl p-6 md:p-8 mb-4">
                <p className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-center font-bold text-gray-800">
                  {lang === 'es' ? '¡' : ''}{result.animal.emoji} {result.number} {getPlural(result.animal, result.number)} {lang === 'es' ? result.situation.es : result.situation.en}!
                </p>
              </div>

              <div className="bg-blue-100 rounded-2xl p-6 mb-4">
                <p className="text-2xl md:text-3xl text-gray-600 mb-2">{t('funFact')}</p>
                <p className="text-3xl md:text-4xl lg:text-5xl">
                  <span className="font-bold">{result.animal.emoji} {getName(result.animal)}{lang === 'es' ? 's' : 's'}</span> {t('canNormally')} {getPower(result.animal)}!
                </p>
              </div>
            </>
          )}

          {/* Question */}
          <div className="bg-gradient-to-r from-green-100 to-teal-100 rounded-2xl p-6 md:p-8 text-center mb-4">
            <p className="text-2xl md:text-3xl text-gray-600 mb-2">{t('thinkAbout')}</p>
            <p className="text-4xl md:text-5xl lg:text-6xl font-bold text-teal-700">
              {lang === 'es' ? result.question.es : result.question.en}
            </p>
          </div>

          {/* Draw Your Own Button */}
          <button
            onClick={() => setShowDrawing(!showDrawing)}
            className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-4 px-8 text-3xl md:text-4xl rounded-full shadow-lg transform hover:scale-105 transition-all"
          >
            {showDrawing ? t('hideDrawing') : t('drawYourOwn')}
          </button>

          {/* Drawing Canvas */}
          {showDrawing && (
            <div className="mt-4 bg-white bg-opacity-90 rounded-2xl p-6 shadow-xl">
              <h3 className="text-5xl md:text-6xl font-bold text-center text-purple-600 mb-2">
                {t('drawingTitle')}
              </h3>
              <p className="text-center text-3xl md:text-4xl text-gray-600 mb-4">{t('drawingSubtitle')}</p>

              {/* Combo Preview */}
              <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl p-4 mb-4">
                <p className="text-2xl md:text-3xl text-gray-600 mb-2 font-semibold">{lang === 'es' ? 'Tu Combinación:' : 'Your Combination:'}</p>
                <p className="text-3xl md:text-4xl font-bold text-gray-800">{getComboDescription()}</p>
              </div>

              <EnhancedDrawingCanvas ref={canvasRef} language={lang} />
            </div>
          )}
        </div>
      )}

      {/* Instructions - Responsive sizing */}
      {!result && (
        <div className="bg-white bg-opacity-80 rounded-2xl p-6 w-full max-w-md md:max-w-2xl lg:max-w-4xl xl:max-w-5xl text-center">
          <p className="text-2xl text-gray-700">
            {t('instructions')}
          </p>
          <p className="text-md text-gray-600 mt-2">
            ⚡ <strong>{t('superpowers')}:</strong> {t('superpowerDesc')}
          </p>
          <p className="text-md text-gray-600 mt-1">
            🎭 <strong>{t('sillySituations')}:</strong> {t('situationDesc')}
          </p>
        </div>
      )}
    </div>
  );
}
